import { computed, reactive, watch } from 'vue'

function isSettingDefinition(definition) {
  return definition &&
    typeof definition === 'object' &&
    !Array.isArray(definition) &&
    (
      Object.prototype.hasOwnProperty.call(definition, 'default') ||
      typeof definition.decode === 'function' ||
      typeof definition.encode === 'function'
    );
}

function defaultValueFor(definition) {
  const value = isSettingDefinition(definition) ? definition.default : definition;
  if (typeof value === 'function') return value();
  if (Array.isArray(value)) return [...value];
  if (value && typeof value === 'object') return { ...value };
  return value;
}

function parseStoredValue(localValue) {
  try {
    return JSON.parse(localValue);
  }
  catch(e) {
    return localValue;
  }
}

function decodeSetting(definition, localValue) {
  if (localValue == null || localValue === 'undefined') {
    return defaultValueFor(definition);
  }

  const storedValue = parseStoredValue(localValue);
  if (!isSettingDefinition(definition) || typeof definition.decode !== 'function') {
    return storedValue;
  }

  try {
    return definition.decode(storedValue);
  }
  catch(e) {
    if (storedValue !== localValue) {
      try {
        return definition.decode(localValue);
      }
      catch(e2) {
        console.warn("settings: could not decode value", localValue, e2);
        return defaultValueFor(definition);
      }
    }

    console.warn("settings: could not decode value", localValue, e);
    return defaultValueFor(definition);
  }
}

function encodeSetting(definition, value) {
  if (value === undefined) return undefined;

  if (isSettingDefinition(definition) && typeof definition.encode === 'function') {
    return definition.encode(value);
  }

  return JSON.stringify(value);
}

function createSettings() {
  let settings = reactive({});
  
  // Standard localStorage entries; value is default
  const sl = {
    activeRaceEventId:        undefined,
    activeRaceEventSlug:      undefined,
    token:                    undefined,
    tokenExpiresAt:           {default: false, decode: x => x ? new Date(x) : false, encode: x => x ? x.getTime() : undefined },
    unitPreference:           'mi',
    defaultParticipantTab:    'pd-performance',
    notesVisible:             {default: false, decode: x => x === true || x === 'true', encode: x => x.toString() },
    noSplitCrossingsVisible:  {default: false, decode: x => x === true || x === 'true', encode: x => x.toString() },
    raceEventListTab:         'future',
    lastSelectedRaceIds:      {default: [], decode: x => (Array.isArray(x) ? x : String(x).split(',').filter(Boolean)).map(Number), encode: x => x.join(',')},
    recentParticipants:       {
      default: [],
      decode: x => (Array.isArray(x) ? x : [])
        .map(item => ({
          raceEventId: Number(item?.raceEventId),
          participantId: Number(item?.participantId),
        }))
        .filter(item => Number.isFinite(item.raceEventId) && Number.isFinite(item.participantId)),
      encode: x => JSON.stringify((Array.isArray(x) ? x : []).slice(0, 20)),
    },
    planningFastPace:         3.2,
    planningSlowPace:         1.19,
    
    externalDisplayPort:      undefined,

    mapShowProfile:           false,
    mapShowParticipants:      false,
    mapStyle:                 'mapbox://styles/adc/cm1s3x4m2000g01ofdcqv7lil', // studio outdoors dark

    parBarHeight:             300,
  }
  
  // Load preferences from localStorage
  Object.keys(sl).forEach(k => {
    const localValue = window.localStorage[k];
    settings[k] = decodeSetting(sl[k], localValue);
  });

  // Create references that persist to localStorage
  Object.keys(sl).forEach(k => {
    watch(() => settings[k], 
    s => {
      console.debug(`settings: ${k} =`, s);
      const encodedValue = encodeSetting(sl[k], s);
      if (encodedValue === undefined) {
        window.localStorage.removeItem(k);
      }
      else {
        window.localStorage[k] = encodedValue;
      }
    });
  })

  // Per-item computed
  // These must be at the bottom
  settings.wantsMiles = computed({
    get: () => settings.unitPreference == 'mi',
    set: newValue => settings.unitPreference = !!newValue ? 'mi' : 'km'
  })
  
  return settings
}

const settings = createSettings();
export default settings;
